import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';

import {
    EventPieceJointe,
    Memoire,
    PieceComplementaire,
    PieceComplementaireOrigineType,
    PieceJointType,
    PiecePostale
} from '../../entities/piece-jointe';
import {ActivatedRoute, Router} from '@angular/router';

import {Observable, Subscription} from 'rxjs';
import {Location} from '@angular/common';
import {NgForm} from '@angular/forms';
import {ErrorService} from '../../shared/services/error.service';
import {ScrollToService} from '../../shared';
import {FileUploaderModel} from '../../shared/components/file-uploader/file-uploader.model';
import {FileUploaderComponent} from '../../shared/components/file-uploader/file-uploader.component';
import {TrackingService} from '../../shared/services/tracking.service';
import {TrackingAction, TrackingCategory} from '../../shared/model/tracking/tracking.enum';
import {TrackingName} from '../../shared/model/tracking/tracking.model';
import {RequeteService, RequeteSummary} from '../../entities/requete';
import {ParametersService} from '../../shared/services/parameters.service';
import {PAGE_TITLE_SUFFIX} from '../../app.constants';
import {Title} from '@angular/platform-browser';
import {LiveAnnouncer} from '@angular/cdk/a11y';
import {DragulaService} from 'ng2-dragula';

class Document {
    public isTrc = true;
    public dateEvt = new Date();
    public type: string;
    public pieces: EventPieceJointe[] = [];

    constructor(memoire: Memoire, complementaires: PieceComplementaire[], postales: PiecePostale[]) {
        const complementairesDTO = complementaires.map((piece) => piece.toDTO());
        this.pieces.push(...complementairesDTO);

        const postalesDTO = postales.map((piece) => piece.toDTO());
        this.pieces.push(...postalesDTO);

        if (memoire) {
            this.type = 'MEMOIRE_ENVOYE';
            this.pieces.push(memoire.toDTO());
            complementairesDTO.forEach(piece => piece.fileType = PieceJointType.PIECE_JOINTE_DOCUMENT);
        } else if (complementaires.length || postales.length) {
            this.type = 'PJ_ENVOYE';
        }
    }
}


@Component({
    selector: 'jhi-document',
    templateUrl: './document.component.html',
    styleUrls: ['document.component.scss']
})
export class DocumentComponent implements OnInit, OnDestroy {

    requeteSummary: RequeteSummary;
    memoire: Memoire;
    pieceComplementaireArray: PieceComplementaire[] = [];
    piecePostaleArray: PiecePostale[] = [];

    uploadUrl = '';

    conditionRenommage = false;
    isLoading = false;
    isEmptyError = false;
    isError = false;

    private requeteSubscription: any;
    private parameterSubscription: Subscription;

    isDeactivedPopup = false;
    showLengthWarningForFileName = false;
    maxFileNameWithoutExtensionLength: number;
    maxNumberJointsPieces: number;
    actualFileNameLength = 0;

    @ViewChild(NgForm)
    form: NgForm;

    @ViewChild('pieceAdder')
    pieceAdder: FileUploaderComponent;

    // --- drag n drop keyboard accessible
    public selectedItem = null;

    constructor(private route: ActivatedRoute,
                private location: Location,
                private scrollService: ScrollToService,
                private errorService: ErrorService,
                private requeteService: RequeteService,
                private trackingService: TrackingService,
                private parametersService: ParametersService,
                private router: Router,
                private titleService: Title,
                private liveAnnouncer: LiveAnnouncer,
                private dragulaService: DragulaService) {

        this.dragulaService.drop.subscribe(() => {
            this.liveAnnouncer.announce('élément relaché');
        });

        this.dragulaService.drag.subscribe(() => {
            this.liveAnnouncer.announce('élément selectionné ');
        });
    }

    ngOnInit() {
        this.requeteSubscription = this.route.params.subscribe(params => {
            this.requeteService.getRequeteSummary(params.id).subscribe((requeteSummary) => {
                this.requeteSummary = requeteSummary;
                this.titleService.setTitle(`Ajout des documents complémentaires au dossier
                (N°${requeteSummary.numeroRequete})${PAGE_TITLE_SUFFIX}`)
                this.uploadUrl = `/api/requetes/${this.requeteSummary.id}/documents/upload`;
            });
        });

        this.parameterSubscription = this.parametersService
            .getParameters()
            .subscribe(parameters => (
                this.maxFileNameWithoutExtensionLength = parameters.maxFileNameWithoutExtensionLength));
        this.maxNumberJointsPieces = 99;
    }


    /* Memoire/Courrier */
    onMemoireChange(file: FileUploaderModel) {
        if (file) {
            this.memoire = new Memoire(file);
            this.maxNumberJointsPieces = 98;
            // si on uploade d'abords les pièces complémentaires et qu'elles ont atteint le nombre max,
            // on enlève la dernière pièce uploadée
            if (this.pieceComplementaireArray.length === this.maxNumberJointsPieces + 1) {
                this.pieceComplementaireArray.pop();
            }
        } else {
            this.memoire = null;
            this.maxNumberJointsPieces = 99;
        }
    }

    /* Pieces Complementaires */
    onPieceAdd(file: FileUploaderModel) {
        if (file) {
            this.pieceComplementaireArray
            .push(new PieceComplementaire(file, PieceJointType.PIECES, PieceComplementaireOrigineType.DEFAULT));
            this.pieceAdder.deleteFile();
        }
    }

    onRenamePiece(piece: PieceComplementaire, name: string) {
        piece.libelle = name;
    }

    onPieceDelete(index: number, file: FileUploaderModel) {
        if (!file) {
            this.pieceComplementaireArray.splice(index, 1);
        }
    }

    trackByFileApiId(_index: number, item: PieceComplementaire) {
        return item.fileModel.fileApiId;
    }

    moveUp(index: number) {
        if (index > 0) {
            this.swapPiecesComplementaire(index - 1, index);
        }
    }

    moveDown(index: number) {
        if (index < this.pieceComplementaireArray.length - 1) {
            this.swapPiecesComplementaire(index + 1, index);
        }
    }

    private swapPiecesComplementaire(piece1Index: number, piece2Index: number) {
        const piece1 = this.pieceComplementaireArray[piece1Index];
        const piece2 = this.pieceComplementaireArray[piece2Index];
        this.pieceComplementaireArray[piece2Index] = piece1;
        this.pieceComplementaireArray[piece1Index] = piece2;
    }

    /* Envoi des pieces */
    submitForm() {
        if (this.validateForm()) {
            const doc = new Document(this.memoire, this.pieceComplementaireArray, this.piecePostaleArray);

            this.isLoading = true;
            this.isError = false;
            this.requeteService.addDocument(this.requeteSummary.id, doc)
                .subscribe(
                    (res) => {
                        this.trackAddDocument(TrackingAction.SUCCESS);
                        this.isLoading = false;
                        this.requeteService.addEvent(this.requeteSummary.id, res);
                        this.router.navigate([`/historique/${this.requeteSummary.id}`])
                    },
                    (_res) => {
                        this.trackAddDocument(TrackingAction.FAILED);
                        this.isLoading = false;
                        this.isError = true;
                    }
                );
        } else {
            this.scrollService.scrollToFirstError();
        }
    }

    validateForm(): boolean {
        this.isEmptyError = this.isFormEmpty();
        this.errorService.markAllControlsAsTouched(this.form);
        return this.form.valid && !this.isFormEmpty();
    }

    isFormEmpty(): boolean {
        return !this.memoire && this.isPieceEmpty();
    }

    isPieceEmpty(): boolean {
        return this.pieceComplementaireArray.length === 0 && this.piecePostaleArray.length === 0;
    }

    ngOnDestroy() {
        this.requeteSubscription.unsubscribe();
        this.parameterSubscription.unsubscribe();
    }

    openInventaire() {
        const isIE = (window.navigator && window.navigator.msSaveOrOpenBlob);
        let popup;

        if (!isIE) {
            try {
                popup = window.open('', '_blank');
                popup.document.write('<html><body><head></head><p>Chargement en cours...</p> </body></html>');
                popup.focus();
            } catch (e) {
                this.isDeactivedPopup = true;
            }
        }

        this.getInventaire().subscribe(
            blob => {
                if (isIE) {
                    window.navigator.msSaveOrOpenBlob(blob);
                } else {
                    popup.location.href = URL.createObjectURL(blob);
                }
            }
        );

    }

    previous() {
        this.location.back();
    }

    private getInventaire(): Observable<Blob> {
        this.trackInventairePreviewDownload()
        return this.requeteService.generateInventairePreview(this.pieceComplementaireArray, this.piecePostaleArray,
            this.requeteSummary.id);
    }

    private trackInventairePreviewDownload() {
        this.trackingService.trackEvent({
            action: TrackingAction.DOWNLOAD,
            properties: {
                category: TrackingCategory.AJOUT_DOCUMENTS,
                name: TrackingName.PREVIEW_INVENTORY
            }
        });
    }

    checkLengthForFileName(value: string) {
        if (value) {
            this.actualFileNameLength = value.length;
            this.showLengthWarningForFileName = value.length > this.maxFileNameWithoutExtensionLength;
        }
    }

    private trackAddDocument(action: TrackingAction) {
        this.trackingService.trackEvent({
            action,
            properties: {
                category: TrackingCategory.AJOUT_DOCUMENTS,
                name: TrackingName.GENERAL
            }
        });

        if (this.memoire) {
            this.trackingService.trackEvent({
                action,
                properties: {
                    category: TrackingCategory.AJOUT_DOCUMENTS,
                    name: 'Ajout d\'un mémoire'
                }
            });
        }

        if (this.pieceComplementaireArray.length) {
            this.trackingService.trackEvent({
                action,
                properties: {
                    category: TrackingCategory.AJOUT_DOCUMENTS,
                    name: TrackingName.NOMBRE_PIECES_COMPLEMENTAIRES,
                    value: this.pieceComplementaireArray.length
                }
            });
        }

        if (this.piecePostaleArray.length) {
            this.trackingService.trackEvent({
                action,
                properties: {
                    category: TrackingCategory.AJOUT_DOCUMENTS,
                    name: TrackingName.NOMBRE_PIECES_POSTALES,
                    value: this.piecePostaleArray.length
                }
            });
        }
    }

    // --- drag n drop keyboard accessible
    onMoveup(data: any) {
        console.log('onMoveup', data);
        this.moveUp(data.i);
    }

    onMovedown(data: any) {
        console.log('onMovedown', data);
        this.moveDown(data.i);
    }

    onSelect(data: any) {
        console.log('onSelect', data);
        this.selectedItem = data.piece.fileModel.fileApiId;
    }

    onEscape(data: any) {
        console.log('onEscape', data);
        this.selectedItem = null;
    }
}



// WEBPACK FOOTER //
// ./src/main/webapp/app/requete/add-document/document.component.ts